/**
 * Indonesian-aware text normalisation shared by retrieval and the
 * "pertanyaan terpopuler" report. Deliberately small: visitors write in
 * casual chat Indonesian ("kak, harganya brp?"), so expanding slang and
 * dropping particles buys more than a real stemmer would.
 */

const STOPWORDS = new Set(
  (
    "yang dan di ke dari ini itu untuk dengan pada adalah atau juga saja sudah akan " +
    "saya aku kami kita anda kamu kak kakak min admin mas mbak pak bu bang gan sis " +
    "apa apakah ada bisa boleh mau ingin tolong mohon dong ya yah sih kah lah deh nih tuh berapa per " +
    "the a an is are of to and in on for with do does can i you we what get my me your it be have has " +
    "how which will this that there about from by at or if any " +
    "halo hai hi hello permisi selamat pagi siang sore malam terima kasih makasih thanks"
  ).split(" "),
);

/** Chat shorthand → the word the knowledge base actually uses. */
const SLANG: Record<string, string> = {
  brp: "berapa",
  hrg: "harga",
  gk: "tidak",
  ga: "tidak",
  gak: "tidak",
  nggak: "tidak",
  enggak: "tidak",
  tdk: "tidak",
  krn: "karena",
  utk: "untuk",
  dgn: "dengan",
  sm: "sama",
  jg: "juga",
  bs: "bisa",
  udh: "sudah",
  sdh: "sudah",
  blm: "belum",
  wa: "whatsapp",
  ongkir: "ongkos kirim",
  cod: "bayar ditempat",
  alamatnya: "alamat",
  lokasinya: "lokasi",
  jamnya: "jam",
};

/** Groups of words visitors use interchangeably for the same intent. */
const SYNONYM_GROUPS: readonly (readonly string[])[] = [
  // "berapa" is a question word ("berapa lama", "dp berapa persen"), not a price word: it is a stopword.
  ["harga", "biaya", "tarif", "price", "cost", "rp", "budget"],
  ["jam", "buka", "tutup", "operasional", "hours", "open"],
  ["alamat", "lokasi", "dimana", "maps", "address", "location"],
  ["kirim", "pengiriman", "ongkos", "antar", "delivery", "shipping", "ekspedisi"],
  ["bayar", "pembayaran", "transfer", "qris", "payment", "tunai"],
  ["pesan", "order", "beli", "booking", "reservasi", "purchase"],
  ["legal", "legalitas", "nib", "npwp", "akta", "izin", "sertifikat", "halal"],
  ["kontak", "hubungi", "whatsapp", "telepon", "email", "contact"],
  ["lama", "durasi", "hari", "waktu", "estimasi", "selesai"],
  ["meter", "m2", "luas", "persegi"],
  ["dp", "uang muka", "muka", "down payment"],
];

const SYNONYMS = new Map<string, readonly string[]>();
for (const group of SYNONYM_GROUPS) {
  for (const word of group) SYNONYMS.set(word, group);
}

const SUFFIXES = ["nya", "lah", "kah", "pun"];

export function normalize(text: string): string {
  return text
    .replace(/%/g, " persen ")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function stem(word: string): string {
  for (const suffix of SUFFIXES) {
    if (word.length > suffix.length + 3 && word.endsWith(suffix)) return word.slice(0, -suffix.length);
  }
  return word;
}

/** Content-bearing terms of a text, slang expanded and particles dropped. */
export function terms(text: string): string[] {
  const out: string[] = [];
  for (const raw of normalize(text).split(" ")) {
    if (!raw) continue;
    const expanded = SLANG[raw] ?? raw;
    for (const word of expanded.split(" ")) {
      const stemmed = stem(word);
      if (stemmed.length < 2 || STOPWORDS.has(stemmed)) continue;
      out.push(stemmed);
    }
  }
  return out;
}

/**
 * Query terms with weights: the visitor's own words count fully, synonyms
 * count half — so "ongkir ke Bandung?" still finds "pengiriman", but an entry
 * that repeats the visitor's exact words outranks one that merely shares a
 * synonym.
 */
export function expandQuery(text: string): Map<string, number> {
  const base = terms(text);
  const weighted = new Map<string, number>();
  for (const term of base) weighted.set(term, 1);
  for (const term of base) {
    // Multi-word synonyms ("uang muka") match word by word, like the indexed text.
    for (const synonym of (SYNONYMS.get(term) ?? []).flatMap((phrase) => terms(phrase))) {
      if (!weighted.has(synonym)) weighted.set(synonym, 0.5);
    }
  }
  return weighted;
}
